import React, { FC, useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import { Save as SaveIcon, Edit as EditIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import axios from '../../../../common/api/request';
import TimeSeriesChart from './TimeSeriesChart';

interface TimeSeriesDataPoint {
  point: string;
  value: number;
}

interface StratumData {
  priority: boolean;
  format: string;
  mailCategory: string;
  accountingType: string;
  oeFrom: string;
  oeTo: string;
  receptacleCategory: string;
  stratum: string;
  receptaclesReceivedCurrentQuarter: number | null;
  forecastReceptaclesReceivedNextQuarter: number | null;
  expectedSampleSize: number | null;
  accuracyTarget: number | null;
  historicData: TimeSeriesDataPoint[];
  forecastData: TimeSeriesDataPoint[];
  lowerConfidenceLimits80: TimeSeriesDataPoint[];
  upperConfidenceLimits80: TimeSeriesDataPoint[];
  lowerConfidenceLimits95: TimeSeriesDataPoint[];
  upperConfidenceLimits95: TimeSeriesDataPoint[];
  expectedSampleSizeRounded: number | null;
  samplingRateRounded: number | null;
  forecastManuallyOverwritten: boolean;
  samplingRate: number | null;
}

interface ForecastData {
  [key: string]: StratumData[];
}

interface ForecastStratumTableProps {
  data: ForecastData;
  onUpdateSuccess?: (data: any) => void;
  onUpdateError?: (error: any) => void;
}

interface UpdateForecastRequest {
  operatorId: string;
  value: number;
  stratum: string;
  format: string;
  service: boolean;
}

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  '& .MuiTableCell-head': {
    backgroundColor: '#f5f5f5',
    fontWeight: 600,
    fontSize: '0.875rem',
    textAlign: 'center',
  },
  '& .MuiTableCell-body': {
    fontSize: '0.8rem',
    textAlign: 'center',
  },
}));

const ChartModal = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '1000px',
    width: '90vw',
    height: '70vh',
  },
}));

const ForecastInputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  position: 'relative',
  '& .MuiTextField-root': {
    width: '80px',
    '& input': {
      padding: '4px 8px',
      fontSize: '0.8rem',
      textAlign: 'center',
    },
  },
  '&.changed .MuiTextField-root input': {
    backgroundColor: '#cf7272',
    color: '#ffffff',
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  padding: '2px',
  '&.save-icon': {
    position: 'absolute',
    right: '4px',
    top: '50%',
    transform: 'translateY(-50%)',
  },
}));

export const ForecastStratumTable: FC<ForecastStratumTableProps> = ({
  data,
  onUpdateSuccess,
  onUpdateError,
}) => {
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedStratum, setSelectedStratum] = useState<StratumData | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<{
    stratum: string;
    operator: string;
    value: number;
    format: string;
    service: boolean;
  } | null>(null);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [savingStates, setSavingStates] = useState<Record<string, boolean>>({});
  const [refreshedData, setRefreshedData] = useState<ForecastData | null>(null);

  // Reset refreshedData and editedValues when data prop changes (e.g., after HTML/XLS download)
  useEffect(() => {
    setRefreshedData(null);
    setEditedValues({});
  }, [data]);

  // Mutation for updating forecast
  const updateForecastMutation = useMutation({
    mutationFn: async (updateData: UpdateForecastRequest) => {
      // First make the PUT request to update the forecast
      const putResponse = await axios.put('/forecast-stratum/update-forecast-next-quarter', updateData);
      
      // Then automatically make the GET request to refresh the data
      const getResponse = await axios.get('/forecast-stratum');
      
      return {
        putData: putResponse.data,
        getData: getResponse.data
      };
    },
    onSuccess: (data) => {
      setSavingStates(prev => ({ ...prev, [pendingUpdate?.stratum || '']: false }));
      setEditedValues(prev => {
        const newValues = { ...prev };
        delete newValues[pendingUpdate?.stratum || ''];
        return newValues;
      });
      setRefreshedData(data.getData);
      setConfirmModalOpen(false);
      setPendingUpdate(null);
      onUpdateSuccess?.(data.getData);
    },
    onError: (error) => {
      setSavingStates(prev => ({ ...prev, [pendingUpdate?.stratum || '']: false }));
      setConfirmModalOpen(false);
      setPendingUpdate(null);
      onUpdateError?.(error);
    },
  });

  const handleChartClick = (stratumData: StratumData) => {
    setSelectedStratum(stratumData);
    setChartModalOpen(true);
  };

  const handleForecastChange = (stratum: string, value: string) => {
    setEditedValues(prev => ({
      ...prev,
      [stratum]: value,
    }));
  };

  const handleForecastSave = (stratum: string, operator: string, value: string, format: string, service: boolean) => {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0) return;

    setPendingUpdate({
      stratum,
      operator,
      value: numValue,
      format,
      service,
    });
    setConfirmModalOpen(true);
  };

  const handleConfirmUpdate = () => {
    if (pendingUpdate) {
      setSavingStates(prev => ({ ...prev, [pendingUpdate.stratum]: true }));
      updateForecastMutation.mutate({
        operatorId: pendingUpdate.operator,
        value: pendingUpdate.value,
        stratum: pendingUpdate.stratum,
        format: pendingUpdate.format,
        service: pendingUpdate.service,
      });
    }
  };

  const handleCancelUpdate = () => {
    setConfirmModalOpen(false);
    setPendingUpdate(null);
  };

  const isPositiveWholeNumber = (str: string) => {
    const n = ~~Number(str);
    return String(n) === str && n >= 0;
  };


  // Use refreshed data if available, otherwise use original data
  const currentData = refreshedData || data;
  
  // Flatten data for table display
  const tableData = Object.entries(currentData).flatMap(([operator, strata]) =>
    strata.map(stratum => ({ ...stratum, operator }))
  );

  return (
    <>
      <Paper data-testid="forecast-stratum-table-paper">
        <StyledTableContainer data-testid="forecast-stratum-table-container">
        <Table size="small" data-testid="forecast-stratum-table">
          <TableHead data-testid="forecast-stratum-table-head">
            <TableRow data-testid="forecast-stratum-table-header-row">
              <TableCell data-testid="forecast-stratum-table-header-service">Service</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-format">Format</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-mail-category">Mail Category</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-accounting-type">Accounting Type</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-oe-from">OE From</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-oe-to">OE To</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-receptacle-type">Receptacle Type</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-stratum">Stratum</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-time-series">Time series *</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-current-quarter">Current Quarter</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-forecast-next-quarter">Forecast Next Quarter **</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-expected-sample-size">Expected Sample Size ***</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-sampling-rate">Sampling Rate</TableCell>
              <TableCell data-testid="forecast-stratum-table-header-accuracy-target">Accuracy Target</TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-testid="forecast-stratum-table-body">
            {tableData.map((row, index) => (
              <TableRow key={`${row.operator}-${row.stratum}-${index}`} data-testid={`forecast-stratum-table-row-${index}`}>
                <TableCell data-testid={`forecast-stratum-table-cell-service-${index}`}>{row.priority ? 'Priority' : 'Non-Priority'}</TableCell>
                <TableCell data-testid={`forecast-stratum-table-cell-format-${index}`}>{row.format}</TableCell>
                <TableCell data-testid={`forecast-stratum-table-cell-mail-category-${index}`}>{row.mailCategory}</TableCell>
                <TableCell data-testid={`forecast-stratum-table-cell-accounting-type-${index}`}>{row.accountingType}</TableCell>
                <TableCell data-testid={`forecast-stratum-table-cell-oe-from-${index}`}>{row.oeFrom}</TableCell>
                <TableCell data-testid={`forecast-stratum-table-cell-oe-to-${index}`}>{row.oeTo}</TableCell>
                <TableCell data-testid={`forecast-stratum-table-cell-receptacle-category-${index}`}>{row.receptacleCategory || '-'}</TableCell>
                <TableCell className="stratum" data-testid={`forecast-stratum-table-cell-stratum-${index}`}>{row.stratum}</TableCell>
                <TableCell sx={{ minWidth: '200px' }} data-testid={`forecast-stratum-table-cell-time-series-${index}`}>
                  <TimeSeriesChart
                    historicData={row.historicData}
                    forecastData={row.forecastData}
                    lowerConfidenceLimits80={row.lowerConfidenceLimits80}
                    upperConfidenceLimits80={row.upperConfidenceLimits80}
                    lowerConfidenceLimits95={row.lowerConfidenceLimits95}
                    upperConfidenceLimits95={row.upperConfidenceLimits95}
                    width={200}
                    height={60}
                    onClick={() => handleChartClick(row)}
                    isInline={true}
                    data-testid={`forecast-stratum-time-series-chart-${index}`}
                  />
                </TableCell>
                <TableCell data-testid={`forecast-stratum-table-cell-current-quarter-${index}`}>{row.receptaclesReceivedCurrentQuarter ?? '-'}</TableCell>
                <TableCell data-testid={`forecast-stratum-table-cell-forecast-next-quarter-${index}`}>
                  <ForecastInputContainer 
                    className={`next-quarter-cell ${
                      editedValues[row.stratum] && !isPositiveWholeNumber(editedValues[row.stratum]) 
                        ? 'invalid-number' 
                        : ''
                    } ${editedValues[row.stratum] ? 'changed' : ''}`}
                    data-testid={`forecast-stratum-forecast-input-container-${index}`}
                  >
                    <TextField
                      value={editedValues[row.stratum] ?? row.forecastReceptaclesReceivedNextQuarter ?? ''}
                      onChange={(e) => handleForecastChange(row.stratum, e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleForecastSave(
                            row.stratum,
                            row.operator,
                            editedValues[row.stratum] ?? row.forecastReceptaclesReceivedNextQuarter?.toString() ?? '',
                            row.format,
                            row.priority
                          );
                        }
                      }}
                      size="small"
                      variant="outlined"
                      disabled={savingStates[row.stratum]}
                      data-testid={`forecast-stratum-forecast-input-${index}`}
                    />
                    {editedValues[row.stratum] && isPositiveWholeNumber(editedValues[row.stratum]) && (
                      <StyledIconButton
                        className="save-icon"
                        size="small"
                        onClick={() => handleForecastSave(
                          row.stratum,
                          row.operator,
                          editedValues[row.stratum] ?? '',
                          row.format,
                          row.priority
                        )}
                        disabled={savingStates[row.stratum]}
                        data-testid={`forecast-stratum-save-button-${index}`}
                      >
                        <SaveIcon fontSize="small" />
                      </StyledIconButton>
                    )}
                  </ForecastInputContainer>
                </TableCell>
                <TableCell data-testid={`forecast-stratum-table-cell-expected-sample-size-${index}`}>{row.expectedSampleSizeRounded ?? '-'}</TableCell>
                <TableCell data-testid={`forecast-stratum-table-cell-sampling-rate-${index}`}>{row.samplingRateRounded ?? '-'}</TableCell>
                <TableCell data-testid={`forecast-stratum-table-cell-accuracy-target-${index}`}>
                  {row.accuracyTarget ? `${row.accuracyTarget}%` : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </StyledTableContainer>
      </Paper>

      {/* Chart Modal */}
      <ChartModal
        open={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        maxWidth="lg"
        fullWidth
        data-testid="forecast-stratum-chart-modal"
      >
        <DialogTitle data-testid="forecast-stratum-chart-modal-title">
          <Typography variant="h6" data-testid="forecast-stratum-chart-modal-title-text">
            Time Series Chart - {selectedStratum?.stratum}
          </Typography>
        </DialogTitle>
        <DialogContent data-testid="forecast-stratum-chart-modal-content">
          {selectedStratum && (
            <Box sx={{ width: '100%', height: '500px', p: 2 }} data-testid="forecast-stratum-chart-container">
              <TimeSeriesChart
                historicData={selectedStratum.historicData}
                forecastData={selectedStratum.forecastData}
                lowerConfidenceLimits80={selectedStratum.lowerConfidenceLimits80}
                upperConfidenceLimits80={selectedStratum.upperConfidenceLimits80}
                lowerConfidenceLimits95={selectedStratum.lowerConfidenceLimits95}
                upperConfidenceLimits95={selectedStratum.upperConfidenceLimits95}
                width={900}
                height={500}
                showPointLabels={true}
                isInline={false}
                data-testid="forecast-stratum-chart-modal-chart"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions data-testid="forecast-stratum-chart-modal-actions">
          <Button onClick={() => setChartModalOpen(false)} variant="contained" data-testid="forecast-stratum-chart-modal-close-button">
            Close
          </Button>
        </DialogActions>
      </ChartModal>

      {/* Confirmation Modal */}
      <Dialog
        open={confirmModalOpen}
        onClose={handleCancelUpdate}
        maxWidth="sm"
        fullWidth
        data-testid="forecast-stratum-confirmation-modal"
      >
        <DialogTitle data-testid="forecast-stratum-confirmation-modal-title">
          <Typography variant="h6" data-testid="forecast-stratum-confirmation-modal-title-text">
            Confirm Forecast Update
          </Typography>
        </DialogTitle>
        <DialogContent data-testid="forecast-stratum-confirmation-modal-content">
          <Typography variant="body1" data-testid="forecast-stratum-confirmation-modal-message">
            Are you sure you want to update the forecast for stratum "{pendingUpdate?.stratum}" 
            to {pendingUpdate?.value}?
          </Typography>
        </DialogContent>
        <DialogActions data-testid="forecast-stratum-confirmation-modal-actions">
          <Button 
            onClick={handleCancelUpdate} 
            variant="outlined"
            disabled={updateForecastMutation.isPending}
            data-testid="forecast-stratum-confirmation-modal-cancel-button"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmUpdate} 
            variant="contained"
            disabled={updateForecastMutation.isPending}
            data-testid="forecast-stratum-confirmation-modal-confirm-button"
          >
            {updateForecastMutation.isPending ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ForecastStratumTable;
